import { useCallback, useEffect, useRef, type ButtonHTMLAttributes } from "react";
import { useTranslation } from "react-i18next";
import { showToast } from "./toast";

/** How long after a press a change of `saved` still counts as its result. */
const ARM_MS = 600;

/**
 * Say "Saved to your list" (with Undo) or "Removed from your list" when a
 * Save press actually changes the saved state.
 *
 * The toast waits for `saved` to change rather than trusting the press: a
 * signed-out guest's press opens the sign-in prompt instead, and saying
 * "Saved" then would be a lie. A change that no press caused (the list
 * syncing from another tab, the page loading the saved slugs) stays quiet,
 * and so does Undo itself.
 *
 * Undo calls the latest `toggle`, not the one from the press, because the
 * page's toggle closes over the saved state it was rendered with.
 */
export function useSaveToast(saved: boolean, toggle: (() => void) | undefined): () => void {
  const { t } = useTranslation();
  const toggleRef = useRef(toggle);
  toggleRef.current = toggle;
  const armedUntil = useRef(0);
  const prev = useRef(saved);

  useEffect(() => {
    if (prev.current === saved) return;
    prev.current = saved;
    if (Date.now() > armedUntil.current) return;
    armedUntil.current = 0;
    if (saved) {
      showToast({
        message: t("activity.toast_saved", { defaultValue: "Saved to your list" }),
        actionLabel: t("activity.toast_undo", { defaultValue: "Undo" }),
        onAction: () => toggleRef.current?.(),
      });
    } else {
      showToast({ message: t("activity.toast_removed", { defaultValue: "Removed from your list" }) });
    }
  }, [saved, t]);

  return useCallback(() => {
    armedUntil.current = Date.now() + ARM_MS;
    toggleRef.current?.();
  }, []);
}

export interface SaveToastButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> {
  saved: boolean;
  onToggle: () => void;
}

/** A Save button that toasts its result, for places that cannot call the hook themselves. */
export function SaveToastButton({ saved, onToggle, type = "button", ...rest }: SaveToastButtonProps) {
  const press = useSaveToast(saved, onToggle);
  return <button type={type} onClick={press} {...rest} />;
}
